// ═══════════════════════════════════════════════════════════════
// PublicArchiveSection.tsx – a nyilvános portál Archívum része
// A szervezet belső Archívumából kérésre közzétett tételek (a nyitólapon mind, egy oldalon az oldalhoz
// tartozók). Ha nincs közzétett archív tétel, nem jelenik meg semmi.
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { ApiClient, type PublicArchiveItem } from '../../api/client';
import { RichTextView } from '../common/RichTextView';
import { Archive, Paperclip, ChevronDown, ChevronRight } from 'lucide-react';

const fmtDay = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(`${s.slice(0, 10)}T00:00:00Z`);
  return isNaN(+d) ? s : d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).replace(/\.$/, '');
};

export const PublicArchiveSection: React.FC<{ tenantSlug: string; pageId?: number | null }> = ({ tenantSlug, pageId }) => {
  const [items, setItems] = useState<PublicArchiveItem[]>([]);
  const [open, setOpen] = useState<Record<number, boolean>>({});

  useEffect(() => {
    let alive = true;
    ApiClient.getPublicArchive(tenantSlug).then((r) => { if (alive && r.ok) setItems(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, [tenantSlug]);

  const list = pageId ? items.filter((i) => i.page_id === pageId) : items;
  if (!list.length) return null;

  return (
    <section aria-labelledby="public-archive-title" className="mt-8">
      <h2 id="public-archive-title" className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
        <Archive className="w-4 h-4 text-amber-600" /> Archívum – kérésre közzétett korábbi adatok
      </h2>
      <div className="space-y-2">
        {list.map((it) => {
          const isOpen = Boolean(open[it.id]);
          return (
            <div key={it.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <button type="button" aria-expanded={isOpen} onClick={() => setOpen((o) => ({ ...o, [it.id]: !o[it.id] }))}
                className="w-full text-left px-4 py-3 flex items-start gap-2 cursor-pointer">
                {isOpen ? <ChevronDown className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />}
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-900 dark:text-white break-words">{it.title || '(cím nélkül)'}</span>
                  <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                    {!pageId && it.page_title ? `${it.page_title} · ` : ''}Archiválva: {fmtDay(it.archived_at)}
                    {it.retain_until ? ` · Megőrzés: ${fmtDay(it.retain_until)}-ig` : ''}
                  </span>
                </span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pl-10 space-y-3">
                  {it.records.map((r) => (
                    <div key={r.id} className="text-sm text-slate-700 dark:text-slate-300 space-y-1.5">
                      {(it.records.length > 1 || it.kind === 'page') && (
                        <div className="font-semibold text-slate-900 dark:text-white">{[r.data.pont, r.data.cim || r.data.megnevezes].filter(Boolean).join(' – ')}{r.year ? ` (${r.year})` : ''}</div>
                      )}
                      {(r.data.leiras_html || r.data.leiras || r.data.ertek) && <RichTextView html={r.data.leiras_html} text={String(r.data.leiras || r.data.ertek || '')} />}
                      {r.attachments.length > 0 && (
                        <ul className="flex flex-col gap-1">
                          {r.attachments.map((a) => (
                            <li key={a.id}>
                              <a href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-600 dark:text-cyan-400 hover:underline text-xs">
                                <Paperclip className="w-3 h-3" /> {a.filename}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                  {it.attachments.length > 0 && (
                    <ul className="flex flex-col gap-1">
                      {it.attachments.map((a) => (
                        <li key={a.id}>
                          <a href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-600 dark:text-cyan-400 hover:underline text-xs">
                            <Paperclip className="w-3 h-3" /> {a.filename}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PublicArchiveSection;
